import axios from "axios";
import { createSessionManager } from "./session.mjs";
import { createGoogleOAuth } from "./google-oauth.mjs";
import { consumeChatStream } from "./chat-stream.mjs";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API = `${BACKEND_URL}/api`;

const session = createSessionManager(localStorage, async (refresh_token) => {
  const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token });
  return data;
});
export const getToken = () => localStorage.getItem("sp_token");
export const setToken = (token) => session.save({ token });
export const clearToken = () => {
  session.clear();
  for (const key of Object.keys(sessionStorage)) {
    if (key.startsWith("sp_supabase")) sessionStorage.removeItem(key);
  }
};
const auth = async () => ({ headers: { Authorization: `Bearer ${await session.accessToken()}` } });

export const connect = async () => {
  const { data } = await axios.post(`${API}/auth/connect`);
  session.save(data);
  localStorage.setItem("sp_email", data.email);
  localStorage.removeItem("sp_demo");
  return data;
};

export const connectDemo = async () => {
  const { data } = await axios.post(`${API}/auth/demo`);
  session.save({ ...data, is_demo: true });
  return data;
};

export const registerUser = async (email, password, name) => {
  const { data } = await axios.post(`${API}/auth/register`, { email, password, name });
  if (!data.confirmation_required) session.save(data);
  return data;
};

export const loginUser = async (email, password) => {
  const { data } = await axios.post(`${API}/auth/login`, { email, password });
  session.save(data);
  localStorage.setItem("sp_email", data.email);
  localStorage.removeItem("sp_demo");
  return data;
};

export const getMe = async () => {
  const { data } = await axios.get(`${API}/auth/me`, await auth());
  return data;
};

export const logout = async () => {
  try { await axios.post(`${API}/auth/logout`, {}, await auth()); } catch (e) { /* ignore */ }
  clearToken();
};

export const disconnectGoogle = async () => {
  const { data } = await axios.post(`${API}/auth/google/disconnect`, {}, await auth());
  return data;
};

export const getGoogleConfig = async () => {
  const { data } = await axios.get(`${API}/auth/google/config`);
  return data;
};

let oauthClient;
const getOAuthClient = async (config) => {
  const cfg = config || await getGoogleConfig();
  if (!cfg.supabase_url || !cfg.supabase_publishable_key) throw new Error("Google sign-in is not configured");
  if (!oauthClient) {
    const { createClient } = await import("@supabase/supabase-js");
    oauthClient = createClient(cfg.supabase_url, cfg.supabase_publishable_key, {
      auth: { flowType: "pkce", storage: sessionStorage, storageKey: "sp_supabase_auth",
        persistSession: true, autoRefreshToken: false, detectSessionInUrl: false },
    });
  }
  return oauthClient;
};

const googleOAuth = createGoogleOAuth({
  storage: sessionStorage, getClient: getOAuthClient, origin: () => window.location.origin,
  getUser: getMe, saveSession: data => session.save(data),
  connectData: async payload => (await axios.post(`${API}/auth/google/connect`, payload, await auth())).data,
});
export const startGoogleAuth = (cfg, intent = "login") => googleOAuth.start(cfg, intent);
export const completeGoogleAuth = code => googleOAuth.complete(code);
export const clearGoogleAuth = () => googleOAuth.clear();

export const fetchProperties = async () => {
  const { data } = await axios.get(`${API}/properties`, await auth());
  return data.properties;
};

export const fetchOverview = async (range = 28) => {
  const { data } = await axios.get(`${API}/overview?range=${range}`, await auth());
  return data;
};

export const fetchGa4Properties = async () => {
  const { data } = await axios.get(`${API}/ga4/properties`, await auth());
  return data;
};

export const setGa4Property = async (propertyId, ga4Property) => {
  const { data } = await axios.put(
    `${API}/properties/${encodeURIComponent(propertyId)}/ga4`,
    { ga4_property: ga4Property },
    await auth()
  );
  return data;
};

export const fetchAlerts = async () => {
  const { data } = await axios.get(`${API}/alerts`, await auth());
  return data;
};

export const markAlertsRead = async () => {
  const { data } = await axios.post(`${API}/alerts/read`, {}, await auth());
  return data;
};

export const createFix = async (fix) => {
  const { data } = await axios.post(`${API}/fixes`, fix, await auth());
  return data;
};

export const fetchFixes = async (propertyId) => {
  const { data } = await axios.get(`${API}/fixes?property_id=${encodeURIComponent(propertyId)}`, await auth());
  return data.fixes;
};

export const updateFix = async (fixId, status) => {
  const { data } = await axios.patch(`${API}/fixes/${fixId}`, { status }, await auth());
  return data;
};

export const deleteFix = async (fixId) => {
  const { data } = await axios.delete(`${API}/fixes/${fixId}`, await auth());
  return data;
};

export const fetchDashboard = async (propertyId, range = 28) => {
  const { data } = await axios.get(
    `${API}/dashboard/${encodeURIComponent(propertyId)}?range=${range}`, await auth()
  );
  return data;
};

export const fetchInsights = async (propertyId, refresh = false, range = 28, model = "claude") => {
  const { data } = await axios.post(
    `${API}/insights/${encodeURIComponent(propertyId)}?refresh=${refresh}&range=${range}&model=${model}`,
    {},
    await auth()
  );
  return data;
};

export const fetchModels = async () => {
  const { data } = await axios.get(`${API}/models`, await auth());
  return data;
};

export const fetchSettings = async () => {
  const { data } = await axios.get(`${API}/settings`, await auth());
  return data;
};

export const setPreferredModel = async (model) => {
  const { data } = await axios.put(`${API}/settings/model`, { model }, await auth());
  return data;
};

export const saveApiKey = async (provider, key) => {
  const { data } = await axios.put(`${API}/settings/keys`, { provider, key }, await auth());
  return data;
};

export const removeApiKey = async (provider) => {
  const { data } = await axios.delete(`${API}/settings/keys/${provider}`, await auth());
  return data;
};

// Streaming chat via fetch + SSE parsing
export const streamChat = async (propertyId, message, sessionId, range, model, onDelta, onSession, onDone) => {
  const res = await fetch(`${API}/chat/${encodeURIComponent(propertyId)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${await session.accessToken()}` },
    body: JSON.stringify({ message, session_id: sessionId, range, model }),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.detail || "The chat request failed. Please try again.");
  }
  await consumeChatStream(res.body, { onDelta, onSession, onDone });
};

// Stable shared callback for the persisted workflow components.
export const workflowRequest = async (path, {method = "GET", body, signal} = {}) => {
  const {data} = await axios({url: `${API}${path}`, method, data: body, signal, ...await auth()});
  return data;
};

// Bing Webmaster Tools, read with the user's own API key.
export const fetchBingStatus = async () => {
  const { data } = await axios.get(`${API}/settings/bing`, await auth());
  return data;
};

export const saveBingKey = async (key) => {
  const { data } = await axios.put(`${API}/settings/bing`, { key }, await auth());
  return data;
};

export const removeBingKey = async () => {
  const { data } = await axios.delete(`${API}/settings/bing`, await auth());
  return data;
};

export const fetchBing = async (propertyId, range = 28) => {
  const { data } = await axios.get(`${API}/bing/${encodeURIComponent(propertyId)}?range=${range}`, await auth());
  return data;
};

// Microsoft Clarity, one token per site. Reads come from saved daily snapshots.
export const fetchClarity = async (propertyId, range = 28) => {
  const { data } = await axios.get(`${API}/clarity/${encodeURIComponent(propertyId)}?range=${range}`, await auth());
  return data;
};

export const connectClarity = async (propertyId, token, projectId, range = 28) => {
  const { data } = await axios.put(`${API}/clarity/${encodeURIComponent(propertyId)}?range=${range}`, { token, project_id: projectId || undefined }, await auth());
  return data;
};

export const disconnectClarity = async (propertyId) => {
  const { data } = await axios.delete(`${API}/clarity/${encodeURIComponent(propertyId)}`, await auth());
  return data;
};

export const fetchAiConsent = async site => (await axios.get(`${API}/ai/consent/${encodeURIComponent(site)}`, await auth())).data;
export const grantAiConsent = async (site, provider, version) => (await axios.put(`${API}/ai/consent/${encodeURIComponent(site)}`, { provider, version }, await auth())).data;
export const withdrawAiConsent = async (site, provider) => (await axios.delete(`${API}/ai/consent/${encodeURIComponent(site)}`, { ...(await auth()), data: { provider } })).data;
